import React, { useState, useEffect } from "react";
import { QueryClient, QueryClientProvider, useQuery } from "react-query";
import { IntlProvider } from "react-intl";
import { MathJaxContext } from "better-react-mathjax";

import { initDatadogInteractiveInfographics } from "@labxchange/util-datadog";
import { ImageDiscoveryCanvas } from "@learningblocks/interactives/interactive-infographic-components/lib/ImageDiscoveryCanvas";
import {
  UsersnapProvider,
  loadUsersnap,
  USERSNAP_WIDGETS,
} from "@labxchange/util-usersnap";
import { Translations as fetchComponentTranslations } from "@learningblocks/shared-components/lib/Translations";
import { MediaType } from "@learningblocks/storybook-components/lib/MediaType";
import { HighlightProvider } from "@learningblocks/shared-components/lib/HighlightProvider";
import { QuestionProvider } from "@labxchange/ui-components/lib/QuestionProvider";
import { calculateTooltipPosition } from "@learningblocks/shared-components/lib/HighlightModal/HighlightModal.utils";
import { ImageHotspotProviders } from "@learningblocks/shared-components";

import "./index.css";

const { REACT_APP_IMAGE_HOTSPOT_BASE_ID } = process.env;
const { REACT_APP_LXC_AIRTABLE_API_KEY } = process.env;

// Single QueryClient for the entire tree
const queryClient = new QueryClient();

// Simple loading spinner
const LoadingUI: React.FC = () => (
  <div className="fallback-loading">
    <div className="loading-animation1">.</div>
    <div className="loading-animation2">.</div>
    <div className="loading-animation3">.</div>
  </div>
);

interface RecordFields {
  Path: string;
  JSON: string;
  usersnap: any;
}
interface AirtableRecord {
  fields: RecordFields;
}
interface AirtableResponse {
  records: AirtableRecord[];
}

// Inner component: has the useQuery logic, loading/error states, and renders the canvas
function SceneWrapInner(): React.ReactElement {
  // — Translations state
  const [lang, setLang] = useState("en");
  const [messages, setMessages] = useState<Record<string, string>>({});

  // Initialize Datadog + load translations once
  useEffect(() => {
    initDatadogInteractiveInfographics("image-hotspots-preview");

    (async () => {
      const params = new URLSearchParams(window.location.search);
      const requestedLang = params.get("lng") || "en";

      const compRes = await fetchComponentTranslations(requestedLang);

      setLang(requestedLang);
      setMessages({
        ...compRes.default,
      });
    })();
  }, []);

  // — Parse ?project=… from URL
  const urlParams = new URLSearchParams(window.location.search);
  const projectParam = urlParams.get("project");

  if (!projectParam) {
    throw new Error(
      "Missing project parameter. Please add a project parameter to the URL. Example: ?project=your-project-name"
    );
  }

  const project = projectParam.replace(/%20/g, " ");

  // — Fetch Airtable data
  const { data, isLoading, isError } = useQuery<AirtableResponse, Error>(
    ["sceneData", project],
    async () => {
      const res = await fetch(
        `https://api.airtable.com/v0/${REACT_APP_IMAGE_HOTSPOT_BASE_ID}/Projects?view=Active`,
        {
          headers: {
            Authorization: `Bearer ${REACT_APP_LXC_AIRTABLE_API_KEY}`,
          },
        }
      );
      if (!res.ok) {
        throw new Error(`Airtable returned ${res.status}`);
      }
      return res.json();
    },
    { retry: false }
  );

  // — Loading state
  if (isLoading) return <LoadingUI />;

  // — Network error or no records at all
  if (isError || !data?.records) {
    throw new Error(
      "Sorry, we couldn't load data from Airtable. Airtable may be down or the credentials are missing or invalid."
    );
  }

  // — "Valid" check, mirroring original forEach logic
  let valid = false;
  data.records.forEach((rec) => {
    if (rec.fields.Path === project) {
      valid = true;
    }
  });
  if (!valid) {
    throw new Error(`No active project found matching "${project}"`);
  }

  // — Now safe to find the actual record
  const record = data.records.find((rec) => rec.fields.Path === project)!;

  // — Parse the JSON payload
  let sceneData;
  try {
    sceneData = JSON.parse(record.fields.JSON);
  } catch (error) {
    throw new Error(
      `Failed to parse scene data for project "${project}". The JSON data may be malformed.`
    );
  }

  return (
    <MathJaxContext>
      <MediaType.Provider>
        <HighlightProvider.Component
          calculateTooltipPosition={calculateTooltipPosition}
        >
          <UsersnapProvider
            onLoadCallback={(event: any, api: any) => {
              const widgets = [
                USERSNAP_WIDGETS.IMAGE_HOTSPOT_INTERNAL_FEEDBACK_STAGE,
                USERSNAP_WIDGETS.IMAGE_HOTSPOT_USER_FEEDBACK_PROD,
                USERSNAP_WIDGETS.IMAGE_HOTSPOT_SATISFACTION_SCORE,
              ];

              // Add custom widget from sceneData if it exists
              if (sceneData.usersnap) {
                widgets.push(sceneData.usersnap);
              }

              loadUsersnap(event, api, widgets);
            }}
          >
            <div role="main" className="canvas-standalone-container">
              <ImageHotspotProviders>
                <IntlProvider locale={lang} messages={messages}>
                  <QuestionProvider>
                    <ImageDiscoveryCanvas
                      sceneData={sceneData}
                      preview={true}
                      standalone={true}
                    />
                  </QuestionProvider>
                </IntlProvider>
              </ImageHotspotProviders>
            </div>
          </UsersnapProvider>
        </HighlightProvider.Component>
      </MediaType.Provider>
    </MathJaxContext>
  );
}

// Exported component: wraps the inner component in QueryClientProvider
export function SceneWrap(): React.ReactElement {
  return (
    <QueryClientProvider client={queryClient}>
      <SceneWrapInner />
    </QueryClientProvider>
  );
}
